<template>
  <div class="purchase-credits">
    <div class="container py-4">
      <h2 class="mb-4">{{ $t('purchase_credits.title') }}</h2>

      <div class="row">
        <!-- Section 1: Generate New Invoice -->
        <div class="col-md-5 mb-4">
          <div class="card shadow-sm h-100">
            <div class="card-header bg-primary text-white">
              <h5 class="mb-0">{{ $t('purchase_credits.purchase_card') }}</h5>
            </div>
            <div class="card-body">
              <div v-if="loadingConfig || loadingCustomer" class="text-center py-3">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
              </div>
              <div v-else>
                <!-- Customer (Buyer) Details -->
                 <div class="mb-4">
                   <div class="d-flex justify-content-between align-items-center mb-1">
                      <h6 class="text-muted text-uppercase small fw-bold">{{ $t('purchase_credits.buyer_section') }}</h6>
                      <button class="btn btn-link btn-sm p-0 text-decoration-none" @click="showEditModal">
                        {{ $t('purchase_credits.edit_details') }}
                      </button>
                   </div>
                   <div class="p-2 border rounded">
                      <p class="mb-0 fw-bold">{{ customerDetails.name || 'Your Company Name' }}</p>
                      <p class="mb-0 small" v-if="customerDetails.reg_comert">Reg. Com: {{ customerDetails.reg_comert }}</p>
                      <p class="mb-0 small">{{ customerDetails.address || 'Address not set' }}</p>
                      <p class="mb-0 small">
                        <span v-if="customerDetails.city">{{ customerDetails.city }}</span>
                        <span v-if="customerDetails.county">, {{ customerDetails.county }}</span>
                        <span v-if="customerDetails.country">, {{ customerDetails.country }}</span>
                      </p>
                      <p class="mb-0 small">VAT: {{ customerDetails.vat_id || '-' }}</p>
                      <p class="mb-0 small text-primary">{{ currentUserEmail }}</p>
                   </div>
                </div>
                
                <hr>

                <!-- Calculator -->
                <div class="mb-3">
                  <label for="creditsQty" class="form-label">{{ $t('purchase_credits.quantity_label') }}</label>
                  <input type="number" id="creditsQty" v-model.number="creditsQty" class="form-control" min="1" step="1">
                </div>
                
                <div class="mb-3">
                  <label for="poNumber" class="form-label">{{ $t('purchase_credits.po_label') }}</label>
                  <input type="text" id="poNumber" v-model="poNumber" class="form-control" placeholder="e.g., PO-2024-001">
                </div>

                <div class="d-flex justify-content-between align-items-center mb-4 p-3 bg-light rounded border border-primary bg-primary-subtle">
                   <span class="small">{{ $t('purchase_credits.price') }} <strong>{{ formatCurrency(creditCost) }}</strong> / credit</span>
                   <div class="d-flex align-items-center">
                      <h6 class="mb-0 me-2 text-primary">{{ $t('purchase_credits.total') }}</h6>
                      <h4 class="mb-0 text-primary">{{ formatCurrency(totalCost) }}</h4>
                   </div>
                </div>

                <!-- Action -->
                <div v-if="error" class="alert alert-danger">{{ error }}</div>
                
                <button 
                  class="btn btn-success w-100 btn-lg" 
                  @click="generateInvoice" 
                  :disabled="loading || creditsQty < 1"
                >
                  <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
                  {{ $t('purchase_credits.generate_btn') }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Section 2: Invoice List -->
        <div class="col-md-7 mb-4">
          <div class="card shadow-sm h-100">
            <div class="card-header bg-primary text-white">
              <h5 class="mb-0">{{ $t('purchase_credits.invoice_history') }}</h5>
            </div>
            <div class="card-body p-0">
              <div v-if="loadingInvoices" class="text-center py-5">
                 <div class="spinner-border text-secondary" role="status"></div>
              </div>
              <div v-else-if="invoices.length === 0" class="text-center py-5 text-muted">
                 {{ $t('purchase_credits.no_invoice') }}
              </div>
              <div v-else class="table-responsive">
                <table class="table table-hover mb-0 align-middle">
                  <thead>
                    <tr>
                      <th scope="col">{{ $t('dashboard.table.reference') }}</th>
                      <th scope="col">PO#</th>
                      <th scope="col">{{ $t('dashboard.table.date') }}</th>
                      <th scope="col" class="text-end">{{ $t('dashboard.credits_card.title') }}</th>
                      <th scope="col" class="text-end">{{ $t('dashboard.table.amount') }}</th>
                      <th scope="col" class="text-center">{{ $t('user_management.table.status') }}</th>
                      <th scope="col" class="text-center">{{ $t('user_management.table.actions') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="inv in invoices" :key="inv.reference_id">
                      <td class="small fw-bold">{{ inv.reference_id }}</td>
                      <td class="small">{{ inv.PO_number || '-' }}</td>
                      <td class="small">{{ formatDate(inv.created_at) }}</td>
                      <td class="text-end">{{ inv.credits_amount }}</td>
                      <td class="text-end fw-bold">{{ formatCurrency(inv.invoice_amount) }}</td>
                      <td class="text-center">
                        <span :class="getStatusBadgeClass(inv.status)">
                          {{ inv.status }}
                        </span>
                      </td>
                      <td class="text-center">
                         <button class="btn btn-sm btn-outline-primary" @click="downloadInvoice(inv.reference_id)" title="Download PDF">
                           <i class="bi bi-file-earmark-pdf"></i> {{ $t('purchase_credits.download') }}
                         </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Section 3: Usage History -->
      <div class="row">
        <div class="col-12">
          <div class="card shadow-sm">
            <div class="card-header bg-primary text-white">
              <h5 class="mb-0">{{ $t('purchase_credits.usage_history') }}</h5>
            </div>
            <div class="card-body p-0">
               <div v-if="loadingUsage" class="text-center py-5">
                 <div class="spinner-border text-secondary" role="status"></div>
              </div>
              <div v-else-if="usageHistory.length === 0" class="text-center py-5 text-muted">
                 {{ $t('purchase_credits.no_usage') }}
              </div>
              <div v-else>
                <div class="table-responsive">
                  <table class="table table-hover mb-0 align-middle">
                    <thead>
                      <tr>
                        <th>{{ $t('dashboard.table.date') }}</th>
                        <th>{{ $t('dashboard.table.user') }}</th>
                        <th>{{ $t('dashboard.table.type') }}</th>
                        <th class="text-center">{{ $t('dashboard.table.usage') }}</th>
                        <th>{{ $t('dashboard.table.time') }}</th>
                        <th>{{ $t('dashboard.table.reference') }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="tx in usageHistory" :key="tx.id">
                        <td>{{ formatDate(tx.created_at) }}</td>
                        <td>{{ tx.user_email || 'Unknown' }}</td>
                        <td>
                          <span :class="tx.transaction_type === 'purchase' ? 'text-success fw-bold' : 'text-danger'">
                            {{ tx.transaction_type }}
                          </span>
                        </td>
                         <td class="text-center fw-bold">
                            {{ tx.transaction_type === 'purchase' ? '+' : '-' }}{{ tx.amount }}
                         </td>
                        <td>{{ Math.round(tx.processing_time || 0) }}s</td>
                        <td class="small text-muted">{{ tx.payment_reference || '-' }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                
                <!-- Pagination -->
                <div class="d-flex justify-content-between align-items-center p-3 border-top" v-if="usagePagination.total_pages > 1">
                   <button class="btn btn-sm btn-outline-secondary" 
                     :disabled="usagePagination.page <= 1"
                     @click="changeUsagePage(usagePagination.page - 1)">
                     Previous
                   </button>
                   <span class="small text-muted">Page {{ usagePagination.page }} of {{ usagePagination.total_pages }}</span>
                   <button class="btn btn-sm btn-outline-secondary" 
                     :disabled="usagePagination.page >= usagePagination.total_pages"
                     @click="changeUsagePage(usagePagination.page + 1)">
                     Next
                   </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Details Modal -->
    <div class="modal fade" id="editDetailsModal" tabindex="-1" aria-hidden="true" ref="editModal">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ $t('purchase_credits.edit_modal.title') }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label">{{ $t('purchase_credits.edit_modal.company_name') }}</label>
              <input type="text" class="form-control" v-model="editForm.name">
            </div>
            <div class="mb-3">
              <label class="form-label">{{ $t('purchase_credits.edit_modal.reg_comert') }}</label>
              <input type="text" class="form-control" v-model="editForm.reg_comert">
            </div>
             <div class="mb-3">
              <label class="form-label">{{ $t('purchase_credits.edit_modal.vat_id') }}</label>
              <input type="text" class="form-control" v-model="editForm.vat_id">
            </div>
            <div class="mb-3">
              <label class="form-label">{{ $t('purchase_credits.edit_modal.address') }}</label>
              <textarea class="form-control" rows="2" v-model="editForm.address"></textarea>
            </div>
            <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="form-label">{{ $t('purchase_credits.edit_modal.city') }}</label>
                  <input type="text" class="form-control" v-model="editForm.city">
                </div>
                <div class="col-md-6 mb-3">
                   <label class="form-label">{{ $t('purchase_credits.edit_modal.county') }}</label>
                   <input type="text" class="form-control" v-model="editForm.county">
                </div>
            </div>
             <div class="mb-3">
              <label class="form-label">{{ $t('purchase_credits.edit_modal.country') }}</label>
              <input type="text" class="form-control" v-model="editForm.country">
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">{{ $t('purchase_credits.edit_modal.cancel') }}</button>
            <button type="button" class="btn btn-primary" @click="saveDetails" :disabled="savingDetails">
              <span v-if="savingDetails" class="spinner-border spinner-border-sm me-1"></span>
              {{ $t('purchase_credits.edit_modal.save') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Success Popup -->
    <div v-if="showSuccessPopup" class="success-popup-overlay">
      <div class="success-popup-content text-white position-relative">
        <button type="button" class="btn-close btn-close-white position-absolute top-0 end-0 m-2" @click="showSuccessPopup = false"></button>
        <div class="text-center p-4">
           <i class="bi bi-check-circle-fill fs-1 mb-3 d-block"></i>
           <p class="mb-0 fw-bold">{{ $t('purchase_credits.success_popup.title') }}</p>
           <p class="mb-0 small mt-2">{{ $t('purchase_credits.success_popup.message') }}</p>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import axios from '@/axios'
import { mapGetters } from 'vuex'
// Import bootstrap for modal handling
// Note: We need to assume Bootstrap 5 global or imported. Since it's used elsewhere (App.vue), we'll try to use window.bootstrap or similar.
import { Modal } from 'bootstrap'

export default {
  name: 'PurchaseCredits',
  data() {
    return {
      creditsQty: 100,
      creditCost: 0.1, // Default fallback
      poNumber: '',
      companyDetails: {}, // Issuer
      customerDetails: {}, // Buyer
      
      loadingConfig: true,
      loadingCustomer: true,
      
      invoices: [],
      loadingInvoices: false,

      // Usage History
      usageHistory: [],
      loadingUsage: false,
      usagePagination: {
        page: 1,
        total_pages: 1
      },
      
      loading: false,
      error: null,
      showSuccessPopup: false,

      // Edit Modal
      modalInstance: null,
      savingDetails: false,
      editForm: {
        name: '',
        address: '',
        vat_id: '',
        reg_comert: '',
        city: '',
        county: '',
        country: ''
      }
    }
  },
  computed: {
    ...mapGetters('auth', ['currentUser']),
    totalCost() {
      return (this.creditsQty || 0) * this.creditCost
    },
    currentUserEmail() {
      return this.currentUser ? this.currentUser.email : ''
    }
  },
  mounted() {
    this.fetchConfig()
    this.fetchCustomerDetails()
    this.fetchInvoices()
    this.fetchUsageHistory()
    
    // Initialize modal
    if (this.$refs.editModal) {
      this.modalInstance = new Modal(this.$refs.editModal)
    }
  },
  methods: {
    async fetchConfig() {
      try {
        this.loadingConfig = true
        const res = await axios.get('/api/config/company')
        this.companyDetails = res.data.details
        this.creditCost = res.data.credit_cost
      } catch (e) {
        console.error("Failed to load config", e)
        this.error = "Failed to load pricing configuration."
      } finally {
        this.loadingConfig = false
      }
    },
    async fetchCustomerDetails() {
      try {
        this.loadingCustomer = true
        const res = await axios.get('/api/companies/me', { withCredentials: true })
        this.customerDetails = res.data.company
        // Pre-fill edit form
        this.editForm = { ...this.customerDetails }
      } catch (e) {
        console.error("Failed to load customer details", e)
      } finally {
        this.loadingCustomer = false
      }
    },
    async fetchInvoices() {
      try {
        this.loadingInvoices = true
        const res = await axios.get('/api/invoices', { withCredentials: true })
        this.invoices = res.data.invoices
      } catch (e) {
        console.error("Failed to load invoices", e)
      } finally {
        this.loadingInvoices = false
      }
    },
    async fetchUsageHistory(page = 1) {
      try {
        this.loadingUsage = true
        const res = await axios.get(`/api/credits/history?page=${page}`, { withCredentials: true })
        this.usageHistory = res.data.transactions
        this.usagePagination = res.data.pagination
      } catch (e) {
        console.error("Failed to load usage history", e)
      } finally {
        this.loadingUsage = false
      }
    },
    changeUsagePage(newPage) {
      if (newPage < 1 || newPage > this.usagePagination.total_pages) return
      this.fetchUsageHistory(newPage)
    },
    
    showEditModal() {
      // Refresh form data
      this.editForm = { 
        name: this.customerDetails.name,
        address: this.customerDetails.address,
        vat_id: this.customerDetails.vat_id,
        reg_comert: this.customerDetails.reg_comert,
        city: this.customerDetails.city,
        county: this.customerDetails.county,
        country: this.customerDetails.country
      }
      this.modalInstance.show()
    },
    
    async saveDetails() {
      this.savingDetails = true
      try {
        await axios.put('/api/companies/me', this.editForm, { withCredentials: true })
        // Update local data
        this.customerDetails = { 
          ...this.customerDetails, 
          ...this.editForm 
        }
        this.modalInstance.hide()
      } catch (e) {
        alert("Failed to update details: " + (e.response?.data?.error || e.message))
      } finally {
        this.savingDetails = false
      }
    },

    async generateInvoice() {
      this.error = null
      this.loading = true
      
      try {
        await axios.post('/api/invoices', {
          credits_amount: this.creditsQty,
          po_number: this.poNumber
        }, { withCredentials: true })
        
        await this.fetchInvoices()
        await this.fetchInvoices()
        this.showSuccessPopup = true
        
      } catch (e) {
        this.error = e.response && e.response.data.error ? e.response.data.error : "Failed to create invoice"
      } finally {
        this.loading = false
      }
    },
    formatCurrency(val) {
      if (val === undefined || val === null) return ''
      // USD Currency, US Locale
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val)
    },
    formatDate(dateStr) {
      if (!dateStr) return '-'
      const d = new Date(dateStr)
      return `${d.getDate().toString().padStart(2, '0')}.${(d.getMonth() + 1).toString().padStart(2, '0')}.${d.getFullYear()}`
    },
    getStatusBadgeClass(status) {
      if (status === 'COMPLETED') return 'badge bg-success'
      if (status === 'PENDING') return 'badge bg-warning text-dark'
      return 'badge bg-secondary'
    },
    async downloadInvoice(referenceId) {
       try {
          // Open in new tab or trigger download
          const url = `/api/invoices/${referenceId}/download`;
          // We can just open it in a new window which is simplest for PDF
          window.open(url, '_blank');
          
          // Alternatively via axios if we want to handle errors better
          // But window.open is standard for "View PDF".
          // If downloading:
          // const response = await axios.get(url, { responseType: 'blob' });
          // const link = document.createElement('a');
          // link.href = window.URL.createObjectURL(new Blob([response.data]));
          // link.setAttribute('download', 'invoice.pdf');
          // document.body.appendChild(link);
          // link.click();
       } catch (e) {
         console.error("Download failed", e);
         alert("Failed to download invoice");
       }
    }
  }
}
</script>



<style scoped>
.badge {
  font-size: 0.8em;
}
.success-popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 2000;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0,0,0,0.1); /* Slight dimming of background */
}
.success-popup-content {
    width: 90%;
    max-width: 400px;
    border-radius: 12px;
    background-color: rgba(25, 135, 84, 0.7); /* Green with 30% transparency (70% Opacity) */
    backdrop-filter: blur(4px);
    box-shadow: 0 8px 16px rgba(0,0,0,0.15);
}
</style>
